import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Reservation } from "../../../../utils/api/reservationsAPI";
import "./ReservationMonthView.scss";

type Props = {
  reservations: Reservation[];
  currentMonth: dayjs.Dayjs; // any date in the month
  onDayClick: (date: dayjs.Dayjs) => void;
};

const ReservationMonthView: React.FC<Props> = ({
  reservations,
  currentMonth,
  onDayClick,
}) => {
  const { t } = useTranslation();
  const today = dayjs();

  const monthStart = currentMonth.startOf("month");
  const monthEnd = currentMonth.endOf("month");

  // Days in the calendar grid (pad to start on Monday)
  const calendarDays = useMemo(() => {
    const days: (dayjs.Dayjs | null)[] = [];
    const startPad = (monthStart.day() + 6) % 7; // Mon=0
    for (let i = 0; i < startPad; i++) days.push(null);
    let d = monthStart;
    while (d.isBefore(monthEnd) || d.isSame(monthEnd, "day")) {
      days.push(d);
      d = d.add(1, "day");
    }
    // Pad tail to complete 6-week grid
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [currentMonth]);

  const byDay = useMemo(() => {
    const map = new Map<string, Reservation[]>();
    for (const r of reservations) {
      const key = dayjs(r.date).format("YYYY-MM-DD");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return map;
  }, [reservations]);

  const weekDays = [t("Mon"), t("Tue"), t("Wed"), t("Thu"), t("Fri"), t("Sat"), t("Sun")];

  const statusDot = (status: string) => {
    const colors: Record<string, string> = {
      pending: "#f59e0b",
      confirmed: "#10b981",
      seated: "#3b82f6",
      completed: "#9ca3af",
      cancelled: "#ef4444",
      "no-show": "#6b7280",
    };
    return colors[status] || "#9ca3af";
  };

  return (
    <div className="rmv">
      {/* Weekday headers */}
      <div className="rmv-header">
        {weekDays.map((wd) => (
          <div key={wd} className="rmv-header__day">
            {wd}
          </div>
        ))}
      </div>

      {/* Calendar cells */}
      <div className="rmv-grid">
        {calendarDays.map((day, idx) => {
          if (!day) {
            return <div key={`empty-${idx}`} className="rmv-cell rmv-cell--empty" />;
          }

          const key = day.format("YYYY-MM-DD");
          const items = byDay.get(key) || [];
          const isToday = day.isSame(today, "day");
          const isCurrentMonth = day.month() === currentMonth.month();

          // Take first 3 unique statuses for dots
          const dotStatuses = Array.from(new Set(items.slice(0, 6).map((r) => r.status))).slice(0, 3);

          return (
            <div
              key={key}
              className={`rmv-cell${isToday ? " rmv-cell--today" : ""}${
                !isCurrentMonth ? " rmv-cell--outside" : ""
              }${items.length > 0 ? " rmv-cell--has-reservations" : ""}`}
              onClick={() => onDayClick(day)}
            >
              <span className={`rmv-cell__date${isToday ? " rmv-cell__date--today" : ""}`}>
                {day.format("D")}
              </span>

              {items.length > 0 && (
                <div className="rmv-cell__content">
                  <span className="rmv-cell__count">{items.length}</span>
                  <div className="rmv-cell__dots">
                    {dotStatuses.map((s) => (
                      <span
                        key={s}
                        className="rmv-cell__dot"
                        style={{ background: statusDot(s) }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReservationMonthView;
